import { useSelector } from 'react-redux';
import DialogModal from '../../components/DialogModal';
import { ScoringTypeModalProps, ScoringTypeValue } from './types/scoringType';
import { RootState } from '../../redux/store';
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import MultiValueInput from '../../components/MultiValueInput';
import DropdownSelect from '../../components/DropdownSelect';
import scoringTypes from '../../apis/scoringTypes';
import { StyledButton } from '../Exams/styled';

const SCORING_TYPE_OPTIONS = [
  { value: ScoringTypeValue.NUMERIC, label: 'Numeric' },
  { value: ScoringTypeValue.DROPDOWN, label: 'Dropdown (Valid Scores)' },
  { value: ScoringTypeValue.TEXT, label: 'Text (Free Text)' }
];

const DEFAULT_TEXT_MAX_LENGTH = 500;

const EditScoringTypeModal: React.FC<ScoringTypeModalProps> = ({
  fetchData,
  open,
  onClose,
  scoringType,
  onApiSuccessAndFailure
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState<string>(scoringType?.name || '');
  const [type, setType] = useState<ScoringTypeValue>(
    scoringType?.type || ScoringTypeValue.NUMERIC
  );
  const [validScores, setValidScores] = useState<string[]>(
    scoringType?.validScores || []
  );
  const [maxLength, setMaxLength] = useState<number>(
    scoringType?.maxLength || DEFAULT_TEXT_MAX_LENGTH
  );

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  useEffect(() => {
    setName(scoringType?.name || '');
    setType(scoringType?.type || ScoringTypeValue.NUMERIC);
    setValidScores(scoringType?.validScores || []);
    setMaxLength(scoringType?.maxLength || DEFAULT_TEXT_MAX_LENGTH);
  }, [scoringType]);

  const handleAddScore = useCallback((score: string) => {
    setValidScores((prev) => [...prev, score]);
  }, []);

  const handleRemoveScore = useCallback((indexToRemove: number) => {
    setValidScores((prev) =>
      prev?.filter((_, index) => index !== indexToRemove)
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (!name || !validScores) {
        return;
      }
      const payload = {
        name,
        type,
        validScores:
          type === ScoringTypeValue.DROPDOWN && validScores.length > 0
            ? validScores
            : null,
        ...(type === ScoringTypeValue.TEXT ? { maxLength } : {}),
        id: scoringType?.id
      };
      const response = await scoringTypes.upsertScoringType(
        loggedInUser.school,
        payload
      );
      if (response) {
        fetchData();
        const message = `Scoring type updated successfully`;
        onApiSuccessAndFailure(true, message);
      }
    } catch (error: any) {
      onApiSuccessAndFailure(
        false,
        'Scoring type with this name already exists'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    name,
    type,
    validScores,
    maxLength,
    isSubmitting,
    loggedInUser.school,
    onApiSuccessAndFailure,
    scoringType?.id,
    fetchData
  ]);

  return (
    <>
      <DialogModal
        open={open}
        onClose={onClose}
        title='Edit Scoring Type'
        actions={
          <>
            <Button onClick={onClose} color='primary' variant='contained'>
              Cancel
            </Button>
            <StyledButton
              onClick={handleSubmit}
              disabled={isSubmitting || !name.trim()}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Save'
              )}
            </StyledButton>
          </>
        }
      >
        <Box>
          <TextField
            fullWidth
            id='name'
            name='name'
            label='Scoring Type Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <Box marginTop={2}>
            <DropdownSelect
              label='Data Type'
              placeholder='Select Data Type'
              name='type'
              value={type}
              options={SCORING_TYPE_OPTIONS}
              onChange={(e) =>
                setType(e.target.value as unknown as ScoringTypeValue)
              }
              fullWidth
            />
          </Box>

          {type === ScoringTypeValue.DROPDOWN && (
            <Box marginTop={2}>
              <MultiValueInput
                values={validScores || []}
                onAddValue={handleAddScore}
                onRemoveValue={handleRemoveScore}
                placeholder='Add valid score and press enter'
                label='Valid Scores'
                validationType='text'
              />
            </Box>
          )}

          {type === ScoringTypeValue.TEXT && (
            <Box marginTop={2}>
              <TextField
                fullWidth
                id='maxLength'
                name='maxLength'
                label='Max Length'
                type='number'
                value={maxLength}
                onChange={(e) =>
                  setMaxLength(parseInt(e.target.value, 10) || 0)
                }
              />
            </Box>
          )}
        </Box>
      </DialogModal>
    </>
  );
};

export default EditScoringTypeModal;
